import { BannerType, Screen } from "@next-platform/client-common-core";
import { APPLICATIONS_ID } from "@next-platform/client-common-core/infra/constants/domains";
import { BannerVariant } from "@next-platform/client-common-spicy-ui";

import { EXTERNAL_PERMISSIONS } from "./permissions";

export const CHOOSE_PERSONA_LOCAL_STORAGE_KEY = "isDoNotShowChoosePersona";

export const NavigationLinksConfigType = {
  entry: "entry",
  dropdown: "dropdown",
};

export const CONTENTFUL_SCREENS = {
  REGISTER: "Register",
  PERSONAS: "Personas",
  CREATE_PERSONA: "CreatePersona",
  SALE: "BirthdaySale",
  RECOMMENDATION: "Recommendation",
  FAVORITE_BOTS: "FavoriteChatbots",
  FAVORITES_LOREBOOKS: "FavoritesLorebooks",
  FAVORITES_GROUPS: "FavoritesGroups",
  SUBSCRIBE: "SubscriptionPage", // uses layout banner

  BLOCKED_CREATORS: "BlockedCreator",
  REPORT: "ReportPage",
  SIGN_UP: "WelcomeAboard",
  PROFILE: "ProfileSetting",
  MY_CHATBOTS: "MyChatbots",
  MY_CHATS: "MyChats",
  HOME: "Home",
  EDIT_CHATBOT: "EditChatbot",
  LEADERBOARD: "Leaderboard", // uses layout banner
  TERMS: "Terms",
  COMPLAINT: "Complaint",
  PRIVACY: "Privacy",
  REFUNDS: "Refunds",
  PAGE_2257: "Page2257",
  DOWNLOAD: "Download", // uses layout banner
  PERSONA_EDIT: "EditPersona",
  CHATBOT_PROFILE: "ChatbotProfile",
  CREATOR_PAGE: "CreatorPage",
  SAVED_CHATS: "SavedChats",
  CHAT: "Chat",
  CREATE_CHATBOT: "CreateChatbot",
  CHECKOUT: "Checkout",
};

export const RELATIVE_ROUTES = {
  reportLorebookId: "report/lorebook/:lorebookId",
  reportRulebookId: "report/rulebook/:rulebookId",
  reportChatbot: "report/:characterId",
  androidInstallation: "apps/android",
  accessDenied: "accessDenied",
  voiceCreate: "voice/create",
  voiceClonePreview: "voice/clone-preview/:cloneId",
  voiceCloneError: "voice/error/:id",
  voice: "voice",
  personas: "personas",
  createPersona: "personas/create",
  sale: "sale",
  recommendedBots: "recommended-bots",
  favoriteBots: "favorite-bots",
  favoritesLorebooks: "favorites/lorebooks",
  favoritesGroups: "favorites/groups",
  subscribe: "subscribe",
  getAccess: "get-access",
  blockedCreators: "blocked-creators",
  report: "report", // dynamic
  reportLorebook: "report/lorebook", // dynamic
  reportRulebook: "report/rulebook", // dynamic
  signUp: "signup",
  profile: "profile",
  home: "",
  createChatbot: "chatbot/create",
  chatbotCreateId: "chatbot/create/:draftId",
  chats: "chats",
  discover: "discover",
  chatbots: "my-creations/chatbots",
  myCreationLorebook: "my-creations/lorebooks",
  myCreationRulebook: "my-creations/rulebooks",
  lorebooksExplore: "lorebooks/explore",
  rulebooksExplore: "rulebooks/explore",
  rulebookDetail: "rulebook/:id",
  rulebookEditor: "rulebooks/editor",
  rulebookEditorDraft: "rulebooks/editor/:draftId",
  groupchats: "my-creations/groups",
  voices: "my-creations/voices",
  chatbotsDrafts: "my-creations/chatbots/drafts",
  groupchatsDrafts: "my-creations/groups/drafts",
  groupchatDrafts: "group/drafts",
  leaderboards: "creators/leaderboard",
  terms: "terms",
  complaint: "complaint",
  privacy: "privacy",
  guidelines: "guidelines",
  refund: "refund",
  download: "download",
  compliance: "2257",
  checkout: "checkout",
  checkoutSuccess: "checkout/success",
  externalSignup: "external/signup",
  editChatbot: "chatbot/edit", // dynamic
  personaEdit: "personas/edit", // dynamic
  chatbot: "chatbot", // dyanmic
  creator: "creator", // dynamic
  savedChats: "chats", // dynamic
  chat: "chat", //dynamic
  story: "story", //dynamic
  contribute: "contribute",
  characterCreate: "character/create",
  updateEmail: "update-email",
  welcome: "welcome",
  welcomeSubscribeStar: "welcome/subscribestar",
  linkSubscribeStar: "link/subscribestar",
  linkSubscribeStarId: "link/subscribestar/:transientId",
  userNotFound: "user-not-found",
  lorebookCreate: "lorebook/create",
  lorebookEdit: "lorebook/edit/:id",
  lorebookEditEntries: "lorebook/edit/:id/entries",
  lorebookDetail: "lorebook/:id",
  rulebook: "rulebook", // share/preview prefix, e.g. /rulebook/{id}
  groupchatCreate: "group/create",
  groupchatEdit: "group/edit",
  groupchatProfile: "group/:characterId", // dynamic
  groupChatCreateMembers: "group/create/members",
  portal: "portal",
  manageSubscription: "manage-subscription",
  ageVerification: "age-verification",
  ageVerificationCallback: "age-verification/callback",
  ageVerificationFallback: "age-verification/fallback",
  chatbotTag: "chatbot_tag/:tagId",
  login: "login",
  creatorAnalytics: "creator-analytics",
  myGallery: "my-gallery",
  mediaFile: "media-file/:id",
} as const;

export const ROUTES = {
  androidInstallation: `/${RELATIVE_ROUTES.androidInstallation}`,
  accessDenied: `/${RELATIVE_ROUTES.accessDenied}`,
  voiceCreate: `/${RELATIVE_ROUTES.voiceCreate}`,
  voiceClonePreview: `/${RELATIVE_ROUTES.voiceClonePreview}`,
  voiceError: `/${RELATIVE_ROUTES.voiceCloneError}`,
  voice: `/${RELATIVE_ROUTES.voice}`,
  personas: `/${RELATIVE_ROUTES.personas}`,
  createPersona: `/${RELATIVE_ROUTES.createPersona}`,
  sale: `/${RELATIVE_ROUTES.sale}`,
  recommendedBots: `/${RELATIVE_ROUTES.recommendedBots}`,
  favoriteBots: `/${RELATIVE_ROUTES.favoriteBots}`,
  favoritesLorebooks: `/${RELATIVE_ROUTES.favoritesLorebooks}`,
  favoritesGroups: `/${RELATIVE_ROUTES.favoritesGroups}`,
  subscribe: `/${RELATIVE_ROUTES.subscribe}`,
  getAccess: `/${RELATIVE_ROUTES.getAccess}`,
  blockedCreators: `/${RELATIVE_ROUTES.blockedCreators}`,
  report: `/${RELATIVE_ROUTES.report}`, // dynamic
  reportLorebook: `/${RELATIVE_ROUTES.reportLorebook}`, // dynamic
  signUp: `/${RELATIVE_ROUTES.signUp}`,
  profile: `/${RELATIVE_ROUTES.profile}`,
  home: `/${RELATIVE_ROUTES.home}`,
  createChatbot: `/${RELATIVE_ROUTES.createChatbot}`,
  chatbotCreateId: `/${RELATIVE_ROUTES.chatbotCreateId}`,
  chats: `/${RELATIVE_ROUTES.chats}`,
  discover: `/${RELATIVE_ROUTES.discover}`,
  chatbots: `/${RELATIVE_ROUTES.chatbots}`,
  myCreationLorebook: `/${RELATIVE_ROUTES.myCreationLorebook}`,
  myCreationRulebook: `/${RELATIVE_ROUTES.myCreationRulebook}`,
  lorebooksExplore: `/${RELATIVE_ROUTES.lorebooksExplore}`,
  rulebooksExplore: `/${RELATIVE_ROUTES.rulebooksExplore}`,
  reportRulebook: `/${RELATIVE_ROUTES.reportRulebook}`, // dynamic
  rulebook: `/${RELATIVE_ROUTES.rulebook}`, // dynamic, e.g. /rulebook/{id}
  rulebookEditor: `/${RELATIVE_ROUTES.rulebookEditor}`,
  rulebookEditorDraft: `/${RELATIVE_ROUTES.rulebookEditorDraft}`,
  groupchats: `/${RELATIVE_ROUTES.groupchats}`,
  voices: `/${RELATIVE_ROUTES.voices}`,
  chatbotsDrafts: `/${RELATIVE_ROUTES.chatbotsDrafts}`,
  groupchatsDrafts: `/${RELATIVE_ROUTES.groupchatsDrafts}`,
  groupchatDrafts: `/${RELATIVE_ROUTES.groupchatDrafts}`,
  leaderboards: `/${RELATIVE_ROUTES.leaderboards}`,
  terms: `/${RELATIVE_ROUTES.terms}`,
  complaint: `/${RELATIVE_ROUTES.complaint}`,
  privacy: `/${RELATIVE_ROUTES.privacy}`,
  guidelines: `/${RELATIVE_ROUTES.guidelines}`,
  refund: `/${RELATIVE_ROUTES.refund}`,
  download: `/${RELATIVE_ROUTES.download}`,
  compliance: `/${RELATIVE_ROUTES.compliance}`,
  checkout: `/${RELATIVE_ROUTES.checkout}`,
  checkoutSuccess: `/${RELATIVE_ROUTES.checkoutSuccess}`,
  externalSignup: `/${RELATIVE_ROUTES.externalSignup}`,
  editChatbot: `/${RELATIVE_ROUTES.editChatbot}`, // dynamic
  personaEdit: `/${RELATIVE_ROUTES.personaEdit}`, // dynamic
  chatbot: `/${RELATIVE_ROUTES.chatbot}`, // dyanmic
  creator: `/${RELATIVE_ROUTES.creator}`, // dynamic
  savedChats: `/${RELATIVE_ROUTES.savedChats}`, // dynamic
  chat: `/${RELATIVE_ROUTES.chat}`, //dynamic
  story: `/${RELATIVE_ROUTES.story}`, //dynamic
  contribute: `/${RELATIVE_ROUTES.contribute}`,
  characterCreate: `/${RELATIVE_ROUTES.characterCreate}`,
  updateEmail: `/${RELATIVE_ROUTES.updateEmail}`,
  welcome: `/${RELATIVE_ROUTES.welcome}`,
  welcomeSubscribeStar: `/${RELATIVE_ROUTES.welcomeSubscribeStar}`,
  linkSubscribeStar: `/${RELATIVE_ROUTES.linkSubscribeStar}`,
  linkSubscribeStarId: `/${RELATIVE_ROUTES.linkSubscribeStarId}`,
  userNotFound: `/${RELATIVE_ROUTES.userNotFound}`,
  lorebookCreate: `/${RELATIVE_ROUTES.lorebookCreate}`,
  lorebookEdit: `/${RELATIVE_ROUTES.lorebookEdit}`,
  lorebookEditEntries: `/${RELATIVE_ROUTES.lorebookEditEntries}`,
  lorebookDetail: `/${RELATIVE_ROUTES.lorebookDetail}`,
  groupchatCreate: `/${RELATIVE_ROUTES.groupchatCreate}`,
  groupchatEdit: `/${RELATIVE_ROUTES.groupchatEdit}`,
  groupchatProfile: `/${RELATIVE_ROUTES.groupchatProfile}`, // dynamic
  groupChatCreateMembers: `/${RELATIVE_ROUTES.groupChatCreateMembers}`,
  portal: `/${RELATIVE_ROUTES.portal}`,
  manageSubscription: `/${RELATIVE_ROUTES.manageSubscription}`,
  ageVerification: `/${RELATIVE_ROUTES.ageVerification}`,
  ageVerificationCallback: `/${RELATIVE_ROUTES.ageVerificationCallback}`,
  ageVerificationFallback: `/${RELATIVE_ROUTES.ageVerificationFallback}`,
  chatbotTag: `/${RELATIVE_ROUTES.chatbotTag}`,
  login: `/${RELATIVE_ROUTES.login}`,
  creatorAnalytics: `/${RELATIVE_ROUTES.creatorAnalytics}`,
  myGallery: `/${RELATIVE_ROUTES.myGallery}`,
  mediaFile: `/${RELATIVE_ROUTES.mediaFile}`,
} as const;

export const ROUTE_CONTENTFUL_SCREEN_MAP = {
  [ROUTES.personas]: CONTENTFUL_SCREENS.PERSONAS,
  [ROUTES.createPersona]: CONTENTFUL_SCREENS.CREATE_PERSONA,
  [ROUTES.sale]: CONTENTFUL_SCREENS.SALE,
  [ROUTES.recommendedBots]: CONTENTFUL_SCREENS.RECOMMENDATION,
  [ROUTES.favoriteBots]: CONTENTFUL_SCREENS.FAVORITE_BOTS,
  [ROUTES.favoritesLorebooks]: CONTENTFUL_SCREENS.FAVORITES_LOREBOOKS,
  [ROUTES.favoritesGroups]: CONTENTFUL_SCREENS.FAVORITES_GROUPS,
  [ROUTES.subscribe]: CONTENTFUL_SCREENS.SUBSCRIBE,

  [ROUTES.blockedCreators]: CONTENTFUL_SCREENS.BLOCKED_CREATORS,
  [ROUTES.signUp]: CONTENTFUL_SCREENS.SIGN_UP,
  [ROUTES.profile]: CONTENTFUL_SCREENS.PROFILE,
  [ROUTES.home]: CONTENTFUL_SCREENS.HOME,
  [ROUTES.createChatbot]: CONTENTFUL_SCREENS.CREATE_CHATBOT,
  [ROUTES.chats]: CONTENTFUL_SCREENS.MY_CHATS,
  [ROUTES.chatbots]: CONTENTFUL_SCREENS.MY_CHATBOTS,
  [ROUTES.leaderboards]: CONTENTFUL_SCREENS.LEADERBOARD,
  [ROUTES.terms]: CONTENTFUL_SCREENS.TERMS,
  [ROUTES.complaint]: CONTENTFUL_SCREENS.COMPLAINT,
  [ROUTES.privacy]: CONTENTFUL_SCREENS.PRIVACY,
  [ROUTES.refund]: CONTENTFUL_SCREENS.REFUNDS,
  [ROUTES.download]: CONTENTFUL_SCREENS.DOWNLOAD,
  [ROUTES.compliance]: CONTENTFUL_SCREENS.PAGE_2257,
  [ROUTES.checkout]: CONTENTFUL_SCREENS.CHECKOUT,
};

export const ROUTE_BUSINESS_SCREEN_MAP = {
  [ROUTES.personas]: Screen.PERSONAS,
  [ROUTES.createPersona]: Screen.CREATE_PERSONA,
  [ROUTES.sale]: Screen.SALE,
  [ROUTES.recommendedBots]: Screen.RECOMMENDATION,
  [ROUTES.favoriteBots]: Screen.FAVORITE_BOTS,
  [ROUTES.favoritesLorebooks]: Screen.FAVORITES_LOREBOOKS,
  [ROUTES.favoritesGroups]: Screen.FAVORITES_GROUPS,
  [ROUTES.subscribe]: Screen.SUBSCRIBE,
  [ROUTES.getAccess]: Screen.GET_ACCESS,
  [ROUTES.blockedCreators]: Screen.BLOCKED_CREATORS,
  [ROUTES.signUp]: Screen.SIGN_UP,
  [ROUTES.profile]: Screen.PROFILE,
  [ROUTES.home]: Screen.HOME,
  [ROUTES.discover]: Screen.DISCOVER,
  [ROUTES.createChatbot]: Screen.CREATE_CHATBOT,
  [ROUTES.chats]: Screen.MY_CHATS,
  [ROUTES.chatbots]: Screen.MY_CHATBOTS,
  [ROUTES.leaderboards]: Screen.LEADERBOARD,
  [ROUTES.terms]: Screen.TERMS,
  [ROUTES.complaint]: Screen.COMPLAINT,
  [ROUTES.privacy]: Screen.PRIVACY,
  [ROUTES.refund]: Screen.REFUNDS,
  [ROUTES.download]: Screen.DOWNLOAD,
  [ROUTES.compliance]: Screen.PAGE_2257,
  [ROUTES.checkout]: Screen.CHECKOUT,
  [ROUTES.checkoutSuccess]: Screen.CHECKOUT_SUCCESS,
};

export const DYNAMIC_ROUTE_SCREEN_MAP = {
  [ROUTES.editChatbot]: Screen.EDIT_CHATBOT,
  [ROUTES.personaEdit]: Screen.PERSONA_EDIT,
  [ROUTES.chatbot]: Screen.CHATBOT_PROFILE,
  [ROUTES.creator]: Screen.CREATOR_PAGE,
  [ROUTES.savedChats]: Screen.SAVED_CHATS,
  [ROUTES.report]: Screen.REPORT,
};

export const DYNAMIC_ROUTE_BUSINESS_SCREEN_MAP = {
  [ROUTES.editChatbot]: Screen.EDIT_CHATBOT,
  [ROUTES.personaEdit]: Screen.PERSONA_EDIT,
  [ROUTES.chatbot]: Screen.CHATBOT_PROFILE,
  [ROUTES.creator]: Screen.CREATOR_PAGE,
  [ROUTES.savedChats]: Screen.SAVED_CHATS,
  [ROUTES.report]: Screen.REPORT,
  [ROUTES.chat]: Screen.CHAT,
};

export const EXTERNAL_ROUTES = {
  guidelines: "https://docs.spicychat.ai/community-guidelines",
  affiliates: "https://promote.spicychat.ai/",
  aiModelParameters: "/product-guides/premium-features/ai-models#ai-model-parameters",
};

const SPICYCHAT_DOC_BASE_URL = "https://docs.spicychat.ai";
const PIXELCHAT_DOC_BASE_URL = "https://docs.pixelchat.ai";
const SECRETMATE_DOC_BASE_URL = "https://docs.secretmate.ai";

/* eslint-disable @typescript-eslint/naming-convention -- legacy snake_case property names kept for API compatibility */
type ExternalRoutesConfig = {
  guidelines: string;
  chatbot_creation_guide: string;
  group_creation_guide: string;
  affiliates?: string;
  docs: string;
  blog?: string;
  persona_creation_guide: string;
  create_chatbot_docs: {
    creation_guide: string;
    avatar: string;
    name: string;
    title: string;
    greeting: string;
    personality: string;
    tags: string;
    scenario: string;
    dialogue: string;
  };
  conversation_images_doc: string;
  tts_docs: string;
  custom_voices_docs: string;
  context_window_doc: string;
};
/* eslint-enable @typescript-eslint/naming-convention -- restore default rule */

export const EXTERNAL_ROUTES_FOR_APPLICATION: Record<string, ExternalRoutesConfig> = {
  [APPLICATIONS_ID.SpicyChat]: {
    guidelines: `${SPICYCHAT_DOC_BASE_URL}/community-guidelines`,
    chatbot_creation_guide: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots`,
    group_creation_guide: `${SPICYCHAT_DOC_BASE_URL}/product-guides/group-chat`,
    affiliates: "https://promote.spicychat.ai/",
    docs: SPICYCHAT_DOC_BASE_URL,
    blog: "https://spicychat.ai/pages/blog",
    persona_creation_guide: `${SPICYCHAT_DOC_BASE_URL}/product-guides/premium-features/user-personas`,
    create_chatbot_docs: {
      creation_guide: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots`,
      avatar: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/avatar`,
      name: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/name`,
      title: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/title`,
      greeting: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/greeting`,
      personality: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/personality`,
      tags: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/tags`,
      scenario: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/advanced-settings#scenarios`,
      dialogue: `${SPICYCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/advanced-settings#example-dialogue`,
    },
    conversation_images_doc: `${SPICYCHAT_DOC_BASE_URL}/product-guides/premium-features/conversation-images`,
    tts_docs: `${SPICYCHAT_DOC_BASE_URL}/product-guides/premium-features/text-to-speech`,
    custom_voices_docs: `${SPICYCHAT_DOC_BASE_URL}/product-guides/premium-features/text-to-speech/custom-voices`,
    context_window_doc: `${SPICYCHAT_DOC_BASE_URL}/advanced/tokens-and-context#what-is-a-context-window`,
  },

  [APPLICATIONS_ID.PixelChat]: {
    guidelines: `${PIXELCHAT_DOC_BASE_URL}/community-guidelines`,
    chatbot_creation_guide: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots`,
    group_creation_guide: `${PIXELCHAT_DOC_BASE_URL}/product-guides/group-chat`,
    docs: PIXELCHAT_DOC_BASE_URL,
    persona_creation_guide: `${PIXELCHAT_DOC_BASE_URL}/product-guides/premium-features/multiple-user-personas`,
    create_chatbot_docs: {
      creation_guide: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots`,
      avatar: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/avatar`,
      name: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/name`,
      title: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/title`,
      greeting: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/greeting`,
      personality: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/personality`,
      tags: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/tags`,
      scenario: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/advance-attributes#scenarios`,
      dialogue: `${PIXELCHAT_DOC_BASE_URL}/product-guides/creating-chatbots/advance-attributes#example-dialogue`,
    },
    conversation_images_doc: `${PIXELCHAT_DOC_BASE_URL}/product-guides/premium-features/conversation-images`,
    tts_docs: `${PIXELCHAT_DOC_BASE_URL}/product-guides/premium-features/text-to-speech`,
    custom_voices_docs: `${PIXELCHAT_DOC_BASE_URL}/product-guides/premium-features/text-to-speech/custom-voices`,
    context_window_doc: `${PIXELCHAT_DOC_BASE_URL}/advanced/tokens-and-context#what-is-a-context-window`,
  },

  [APPLICATIONS_ID.SecretMate]: {
    guidelines: `${SECRETMATE_DOC_BASE_URL}/community-guidelines`,
    chatbot_creation_guide: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots`,
    group_creation_guide: `${SECRETMATE_DOC_BASE_URL}/product-guides/group-chat`,
    docs: SECRETMATE_DOC_BASE_URL,
    persona_creation_guide: `${SECRETMATE_DOC_BASE_URL}/product-guides/premium-features/multiple-user-personas`,
    create_chatbot_docs: {
      creation_guide: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots`,
      avatar: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/avatar`,
      name: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/name`,
      title: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/title`,
      greeting: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/greeting`,
      personality: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/personality`,
      tags: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/tags`,
      scenario: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/advance-attributes#scenarios`,
      dialogue: `${SECRETMATE_DOC_BASE_URL}/product-guides/creating-chatbots/advance-attributes#example-dialogue`,
    },
    conversation_images_doc: `${SECRETMATE_DOC_BASE_URL}/product-guides/premium-features/conversation-images`,
    tts_docs: `${SECRETMATE_DOC_BASE_URL}/product-guides/premium-features/text-to-speech`,
    custom_voices_docs: `${SECRETMATE_DOC_BASE_URL}/product-guides/premium-features/text-to-speech/custom-voices`,
    context_window_doc: `${SECRETMATE_DOC_BASE_URL}/advanced/tokens-and-context#what-is-a-context-window`,
  },
};

export const NavigationLinksConfig = [
  {
    type: NavigationLinksConfigType.entry,
    label: "Chats",
    path: "/chats",
    requiredPermissions: [EXTERNAL_PERMISSIONS.view_chats],
  },
  {
    type: NavigationLinksConfigType.entry,
    label: "Create Chatbot",
    path: "/chatbot/create",
    requiredPermissions: [EXTERNAL_PERMISSIONS.create_character],
  },
  {
    type: NavigationLinksConfigType.dropdown,
    label: "My Chatbots",
    requiredPermissions: [EXTERNAL_PERMISSIONS.view_my_chat_bots, EXTERNAL_PERMISSIONS.view_chats],
    entries: [
      {
        type: NavigationLinksConfigType.entry,
        label: "My Chatbots",
        path: `${ROUTES.chatbots}`,
        requiredPermissions: [EXTERNAL_PERMISSIONS.view_my_chat_bots],
      },
      {
        type: NavigationLinksConfigType.entry,
        label: "Favorites",
        path: ROUTES.favoriteBots,
        requiredPermissions: [EXTERNAL_PERMISSIONS.view_chats],
      },
      {
        type: NavigationLinksConfigType.entry,
        label: "Blocked Creators",
        path: ROUTES.blockedCreators,
        requiredPermissions: [EXTERNAL_PERMISSIONS.view_chats],
      },
    ],
  },
  {
    type: NavigationLinksConfigType.entry,
    label: "My Personas",
    path: ROUTES.personas,
    requiredPermissions: [EXTERNAL_PERMISSIONS.view_chats],
  },
  {
    type: NavigationLinksConfigType.entry,
    label: "Help",
    externalLink: "https://docs.spicychat.ai/",
    requiredPermissions: [EXTERNAL_PERMISSIONS.view_help],
  },
];

export const bannerVariantMap = {
  [BannerType.Top]: BannerVariant.TOP,
  [BannerType.Bottom]: BannerVariant.BOTTOM,
  [BannerType.Inline]: BannerVariant.IN_LINE,
  [BannerType.PopUp]: BannerVariant.POP_UP,
};

export const ROUTES_UNAVAILABLE_FOR_PIXELCHAT = [ROUTES.leaderboards, ROUTES.compliance, ROUTES.contribute];
export const ROUTES_UNAVAILABLE_FOR_SECRETMATE = [ROUTES.leaderboards, ROUTES.contribute];
export const LEGAL_PAGES = ["/2257", "/terms", "/privacy", "/refund", ROUTES.complaint];

export const SIDE_NAVIGATION_LOCAL_STORAGE_KEY = "sideNavigation";
export const CREATE_DROPDOWN_LOCAL_STORAGE_KEY = "createDropdown";

export const SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY = "shouldRedirectUser";

export const AUTH_HANDOFF_PARAM = "au_h";
export const AUTH_HANDOFF_VALUE = "1";

export const staticRoutesExcludedForLayoutBanner = [
  ROUTES.home,
  ROUTES.recommendedBots,
  ROUTES.favoriteBots,
  ROUTES.favoritesLorebooks,
  ROUTES.favoritesGroups,
  ROUTES.getAccess,
];

export const PREVIEW_BANNER_KEY = "previewBannerName";

export const BLACKLISTED_APP_HEADER_ROUTES = ["/chat/", "/story/"] as const;
export const NEXT_PAGE_UPON_REDIRECT = "nextpage";

export const GET_PREMIUM_LABEL = "Get Premium";
export const GET_PREMIUM_EVENT_NAME = "nav_header_promo_cta";
